import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { ConversationAiAnalysis, ConversationNote } from '@tips-crm/shared';
import {
  addConversationNote,
  fetchConversationAnalysis,
  fetchConversationArtifacts,
  fetchConversationMessages,
  fetchConversationNotes,
  fetchRecordingUrl,
  setDisposition,
} from '../api/endpoints.js';
import { useAuth } from '../auth/AuthContext.js';
import { Timeline } from './Timeline.js';
import { NoteEditor } from './NoteEditor.js';
import { DispositionForm } from './DispositionForm.js';
import { CallAnalysisCard } from './CallAnalysisCard.js';
import { ErrorState, Loading } from './States.js';

/**
 * Shared conversation detail: timeline + notes + disposition.
 *
 * Transcript/recording artifacts and full message bodies are sensitive. Per
 * SPEC, reseller_user sees summaries/metadata only — so transcript/recording
 * artifacts are hidden and chat message bodies are suppressed for that role.
 * reseller_admin (and platform_admin / agent) see everything.
 */
export function ConversationPanel({
  conversationId,
  currentDisposition,
  conversationStatus,
  compact = false,
}: {
  conversationId: string;
  currentDisposition?: string | null;
  conversationStatus?: string;
  compact?: boolean;
}) {
  const qc = useQueryClient();
  const { hasRole } = useAuth();
  const summariesOnly = hasRole('reseller_user');

  const messagesQ = useQuery({
    queryKey: ['conversation-messages', conversationId],
    queryFn: () => fetchConversationMessages(conversationId),
    enabled: Boolean(conversationId) && !summariesOnly,
  });

  const notesQ = useQuery({
    queryKey: ['conversation-notes', conversationId],
    queryFn: () => fetchConversationNotes(conversationId),
    enabled: Boolean(conversationId),
  });

  const artifactsQ = useQuery({
    queryKey: ['conversation-artifacts', conversationId],
    queryFn: () => fetchConversationArtifacts(conversationId),
    enabled: Boolean(conversationId) && !summariesOnly,
  });

  // Presigned recording URL — 404 until the worker finds the WAV (poll a while).
  const recordingQ = useQuery({
    queryKey: ['recording-url', conversationId],
    queryFn: () => fetchRecordingUrl(conversationId),
    enabled: Boolean(conversationId) && !summariesOnly,
    retry: false,
    refetchInterval: (q) => (q.state.data ? false : 30_000),
  });

  // AI analysis — 404 until the worker produces one; poll until it settles.
  const analysisQ = useQuery<ConversationAiAnalysis>({
    queryKey: ['conversation-analysis', conversationId],
    queryFn: () => fetchConversationAnalysis(conversationId),
    enabled: Boolean(conversationId),
    retry: false,
    refetchInterval: (q) => {
      const status = q.state.data?.status;
      return status === 'completed' || status === 'failed' || status === 'skipped'
        ? false
        : 30_000;
    },
  });

  const addNote = useMutation({
    mutationFn: (body: string) => addConversationNote(conversationId, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['conversation-notes', conversationId] });
    },
  });

  const saveDisposition = useMutation({
    mutationFn: (vars: { disposition: string; note?: string }) =>
      setDisposition(conversationId, vars.disposition, vars.note),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['conversation-notes', conversationId] });
      void qc.invalidateQueries({ queryKey: ['conversation', conversationId] });
      void qc.invalidateQueries({ queryKey: ['conversation-analysis', conversationId] });
    },
  });

  // For reseller_user, surface only AI summary / disposition notes.
  const visibleNotes: ConversationNote[] = (notesQ.data ?? []).filter((n) =>
    summariesOnly ? n.note_type === 'ai_summary' || n.note_type === 'disposition' : true,
  );

  const loading = notesQ.isLoading || (!summariesOnly && (messagesQ.isLoading || artifactsQ.isLoading));
  const errored = notesQ.isError || (!summariesOnly && (messagesQ.isError || artifactsQ.isError));

  if (loading) return <Loading />;
  if (errored) {
    return (
      <ErrorState
        error={notesQ.error ?? messagesQ.error ?? artifactsQ.error}
        onRetry={() => {
          void notesQ.refetch();
          if (!summariesOnly) {
            void messagesQ.refetch();
            void artifactsQ.refetch();
          }
        }}
      />
    );
  }

  return (
    <div className={`conversation-panel${compact ? ' conversation-panel--compact' : ''}`}>
      {summariesOnly && (
        <div className="notice notice--info">
          You have summary-level access. Full transcripts and recordings are restricted.
        </div>
      )}

      {!summariesOnly && (
        <section className="card">
          <h3 className="card__title">Call recording</h3>
          {recordingQ.data ? (
            <audio
              controls
              preload="none"
              src={recordingQ.data.url}
              style={{ width: '100%' }}
            >
              Your browser does not support audio playback.
            </audio>
          ) : (
            <p className="muted">
              No recording yet — voice calls are processed a minute or two after they end.
            </p>
          )}
        </section>
      )}

      <CallAnalysisCard
        conversationId={conversationId}
        analysis={analysisQ.data}
        loading={analysisQ.isLoading}
        conversationStatus={conversationStatus}
        currentDisposition={currentDisposition}
        onApplyDisposition={(d) => void saveDisposition.mutateAsync({ disposition: d })}
        applying={saveDisposition.isPending}
      />

      <section className="card">
        <h3 className="card__title">Timeline {messagesQ.data && messagesQ.data.length > 0 && <span className="muted">· transcript</span>}</h3>
        <Timeline
          messages={summariesOnly ? [] : messagesQ.data ?? []}
          notes={visibleNotes}
          artifacts={summariesOnly ? [] : artifactsQ.data ?? []}
        />
      </section>

      <div className="panel-grid">
        <section className="card">
          <h3 className="card__title">Add note</h3>
          <NoteEditor
            onSubmit={(body) => void addNote.mutateAsync(body)}
            isSubmitting={addNote.isPending}
          />
          {addNote.isError && <ErrorState error={addNote.error} />}
        </section>

        <section className="card">
          <h3 className="card__title">Disposition</h3>
          <DispositionForm
            current={currentDisposition}
            suggested={analysisQ.data?.disposition_suggested ?? null}
            isSubmitting={saveDisposition.isPending}
            onSubmit={(disposition, note) =>
              void saveDisposition.mutateAsync({ disposition, note })
            }
          />
          {saveDisposition.isError && <ErrorState error={saveDisposition.error} />}
        </section>
      </div>
    </div>
  );
}
